import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { DirtyFlagService } from '@ddv/behaviors';
import {
    AlertService,
    ConfirmationPopupService,
    ModalDialogRef,
    ModalDialogService,
    MultiSubscriptionComponent,
} from '@ddv/common-components';
import {
    buildCreateDashboardRequest,
    buildUpdateDashboardInfoRequest,
    DashboardDetails,
    DashboardService,
    DashboardSharingService,
    SaveDashboardFormComponent,
    Sharing,
    ViewLink,
    ViewTile,
} from '@ddv/dashboards';
import { Entitlement, UserData, UserEntitlements, UserService } from '@ddv/entitlements';
import { LayoutService, ManagerService, setWidgetExtraParamsFromDashboardDetails } from '@ddv/layout';
import { ConfirmDialogOptions, DashboardModel, WidgetSnapshot } from '@ddv/models';
import { WidgetOnBoard, WidgetOnBoardService } from '@ddv/widget-on-boards';
import { WidgetConfigurationManager, WidgetFinderComponent } from '@ddv/widgets';
import { Theme, ThemeService } from '@hs/ui-core-presentation';
import { combineLatest, map, switchMap } from 'rxjs';

import { DashboardNavService } from '../../services/dashboard-nav.service';
import { RevertDashboardService } from '../../services/revert-dashboard.service';
import { DashboardFinderComponent } from '../dashboard-finder-dialog/dashboard-finder.component';

@Component({
    selector: 'app-dashboard-nav',
    templateUrl: 'dashboard-nav.component.html',
    styleUrls: ['dashboard-nav.component.scss'],
    standalone: false,
})
export class DashboardNavComponent extends MultiSubscriptionComponent implements OnChanges, OnInit {
    @Input() clientCode: string | undefined;
    @Input() user: UserData | undefined;
    @Input() entitlements: UserEntitlements | undefined;

    @Output() revertChanges = new EventEmitter();
    @Output() dashboardPropertiesUpdated = new EventEmitter<DashboardDetails>();

    protected readonly dirtyFlagService = inject(DirtyFlagService);

    private readonly alertService = inject(AlertService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly confirmationPopupService = inject(ConfirmationPopupService);
    private readonly dashboardService = inject(DashboardService);
    private readonly dashboardNavService = inject(DashboardNavService);
    private readonly layoutService = inject(LayoutService);
    private readonly manager = inject(ManagerService);
    private readonly modalService = inject(ModalDialogService);
    private readonly revertDashboardService = inject(RevertDashboardService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly sharingService = inject(DashboardSharingService);
    private readonly themeService = inject(ThemeService);
    private readonly userService = inject(UserService);
    private readonly widgetOnBoardService = inject(WidgetOnBoardService);

    currentView: ViewLink | undefined;
    inPresentationMode = false;
    copyIsAllowed = false;
    isSideNavExpanded = true;
    showManageWidgetTab = false;
    showManageViewsTab = false;
    showCreateViewTab = false;
    showViewsWrapper: string | number | undefined;
    showFindWidgetIcon = false;
    dashboardEdit = false;
    isGlobalEditPartial = false;
    theme: Theme = Theme.light;

    protected tiles: ViewTile[] = [];

    @ViewChild('dashboardList', { static: false }) private readonly dashboardList: ElementRef | undefined;
    @ViewChild('leftNav', { static: true }) private readonly leftNav: ElementRef<HTMLElement> | undefined;

    private isInternal = false;

    ngOnInit(): void {
        this.initTransitionEvents();

        this.subscribeTo(combineLatest([
            this.route.paramMap,
            this.dashboardNavService.loadViewTiles(),
            this.themeService.currentTheme$,
        ]), ([params, viewTiles, theme]) => {
            this.theme = theme;
            this.initializeViewTiles(params, viewTiles);

            this.showManageWidgetTab = !!this.entitlements?.hasPermission(Entitlement.WIDGET_VIEW);
            this.showManageViewsTab = !!this.entitlements?.hasPermission(Entitlement.DASHBOARD_VIEW);
            this.showCreateViewTab = !!this.entitlements?.hasPermission(Entitlement.DASHBOARD_CREATE);
            this.dashboardEdit = !!this.entitlements?.hasPermission(Entitlement.DASHBOARD_EDIT);
            this.copyIsAllowed = !this.inPresentationMode && !!this.entitlements?.hasPermission(Entitlement.DASHBOARD_CREATE);
        });

        this.subscribeTo(this.manager.isWidgetMaximized$, (isWidgetMaximized: boolean) => {
            this.toggleNavbar(isWidgetMaximized);
        });
    }
    ngOnChanges(): void {
        this.isInternal = !!this.user?.internal;
    }

    onDragEnd(event: CdkDragDrop<ViewTile[]>): void {
        moveItemInArray(this.tiles, event.previousIndex, event.currentIndex);
        const viewIds = this.tiles.reduce((ids: number[], tile) => {
            if (tile.isGroup) {
                ids.push(...tile.links.map((link) => link.id));
            } else {
                ids.push(tile.links[0].id);
            }

            return ids;
        }, []);
        this.userService.updateOpenDashboardOrder(viewIds).subscribe();
    }

    findDashboard(): void {
        const finder = this.modalService.open<void, DashboardFinderComponent>(
            DashboardFinderComponent,
            { windowClass: 'dashboard-finder-popup' }).componentInstance;
        finder.clientCode = this.clientCode;
        finder.entitlements = this.entitlements;
        finder.refetchViewTiles
            .pipe(
                switchMap(() => this.dashboardNavService.fetchOpenViewTiles(this.clientCode ?? '')),
            )
            .subscribe({
                next: (tiles) => {
                    this.initializeViewTiles(this.route.snapshot.paramMap, tiles);
                },
            });
    }

    findWidget(): void {
        if (this.isGlobalEditPartial || !this.currentView) {
            return;
        }

        const dialogRef = this.modalService.open<void, WidgetFinderComponent>(WidgetFinderComponent, { windowClass: 'finder-popup' });
        const popup = dialogRef.componentInstance;
        popup.showOnlyGlobal = !!this.currentView.isGlobal;
        popup.entitlements = this.entitlements;
        popup.clientCode = this.clientCode ?? '';
        popup.widgetAdded.subscribe((widget: WidgetSnapshot) => {
            if (!this.clientCode) {
                return console.error('Cannot add widget to before before client code is set');
            }

            this.widgetOnBoardService.addWidgetToBoard(this.clientCode, this.currentView!.id, widget.id)
                .subscribe({
                    next: (widgetOnBoard) => {
                        this.alertService.success('Successfully added widget to board');
                        this.addWidgetToDashboard(widgetOnBoard);
                        dialogRef.close();
                    },
                    error: () => {
                        this.alertService.error('Failed to add widget to board');
                        dialogRef.close();
                    },
                });
        });
    }

    async switchDashboard(viewId?: number): Promise<void | undefined> {
        if (viewId?.toString() === this.manager.getCurrentDashboardId()) {
            return;
        }

        return this.navigateToBoardAndMode(viewId, this.inPresentationMode ? 'view' : 'edit')
            .then(() => {
                this.manager.selectWidget(false, undefined);
                this.showViewsWrapper = undefined;
            });
    }

    showGroupedViews(e: Event, groupId: string | number): void {
        e.stopPropagation();
        this.showViewsWrapper = this.showViewsWrapper === groupId ? undefined : groupId;
    }

    copyDashboard(event: Event): void {
        event.stopPropagation();
        this.launchCopyDashboardModal();
    }

    showDashboardInfo(e: Event): void {
        e.stopPropagation();

        const dashboard = this.dashboardService.getCurrentDashboard();
        if (dashboard) {
            this.launchSaveForm(dashboard);
        }
    }

    private launchSaveForm(dashboardDetails: DashboardDetails): void {
        const dashboardId = dashboardDetails.id;
        const dialogRef = this.modalService.open<void, SaveDashboardFormComponent>(
            SaveDashboardFormComponent,
            { windowClass: 'save-dialog' });

        dialogRef.componentInstance.dashboard = dashboardDetails;
        dialogRef.componentInstance.dashboardId = dashboardId;
        dialogRef.componentInstance.clientCode = this.clientCode;
        dialogRef.componentInstance.selectedOption = 'edit';
        dialogRef.componentInstance.entitlements = this.entitlements;
        dialogRef.componentInstance.isGlobalEditPartial = this.isGlobalEditPartial;
        dialogRef.componentInstance.dashboardGroupName = this.tiles.find((tile) => tile.selectedLink === this.currentView)?.name ?? '';
        dialogRef.componentInstance.isReadOnly = this.inPresentationMode;
        dialogRef.componentInstance.isInternal = this.isInternal;
        dialogRef.componentInstance.closeForm
            .subscribe({
                next: () => dialogRef.close(),
            });

        dialogRef.componentInstance.formSubmitted.subscribe({
            next: (form: { details: DashboardDetails, sharing: Sharing }) => {
                const { details, sharing } = form;

                if (!this.clientCode || !dashboardId) {
                    return console.warn('Got to saving an existing dashboard without a client code or dashboard id', this.clientCode, dashboardId);
                }

                // When we are done with the migration we can update the dashboard details much earlier
                // in convertFormDataToDashboardDetails method in the component
                const payload = buildUpdateDashboardInfoRequest(details);
                combineLatest([
                    this.dashboardService.updateDashboardInfo(this.clientCode ?? '', dashboardId, payload),
                    this.sharingService.updateSharing(
                        this.clientCode ?? '',
                        dashboardId,
                        sharing,
                        this.clientCode ?? '',
                    ),
                ])
                    .subscribe({
                        next: ([updatedDashboard]) => {
                            updatedDashboard.isGlobal = details.isGlobal;
                            updatedDashboard.widgets = details.widgets;
                            const dashboard = this.manager.getWorkspace();
                            if (dashboard) {
                                setWidgetExtraParamsFromDashboardDetails(updatedDashboard, dashboard);
                            }
                            dialogRef.close();

                            const currentTile = this.tiles.find((tile) => tile.selectedLink?.id === dashboardId);
                            if (currentTile) {
                                const { selectedLink, links } = currentTile;
                                const currentLink = links.find((link) => link.id === dashboardId);
                                if (selectedLink) {
                                    selectedLink.name = updatedDashboard.name;
                                    selectedLink.abbreviation = updatedDashboard.abbreviation;
                                }
                                if (currentLink) {
                                    currentLink.name = updatedDashboard.name;
                                    currentLink.abbreviation = updatedDashboard.abbreviation;
                                }
                            }
                            this.dashboardPropertiesUpdated.emit(updatedDashboard);
                        },
                        error: (error) => this.onDashboardSaveError(error, dialogRef),
                    });

                return;
            },
        });

        dialogRef.result.then(() => {}, () => {});
    }

    toggleNavbar(isWidgetMaximized: boolean | null = null): void {
        this.isSideNavExpanded = isWidgetMaximized != null ? !isWidgetMaximized : !this.isSideNavExpanded;
        const layoutHandler = this.manager.getWorkspaceLayoutHandler();
        if (layoutHandler) {
            setTimeout(() => layoutHandler.onWindowResize(undefined, true), 500);
        }
    }

    isSameView(tile: ViewTile): boolean {
        return tile.selectedLink?.id.toString() === this.currentView?.id.toString();
    }

    protected createDashboard(): void {
        if (this.dirtyFlagService.isDirty) {
            const confirmDialogOptions: ConfirmDialogOptions = {
                message: 'You have un-saved changes. What would you like to do:',
                confirmButtonText: 'Continue Editing',
                denyButtonText: 'Discard Changes',
            };
            this.confirmationPopupService.showConfirmationPopup(confirmDialogOptions, true)
                .subscribe({
                    next: (action) => {
                        if (action !== 'confirm') {
                            this.discardChangesCallback();
                        }
                    },
                });
        } else {
            this.createNewDashboard();
        }
    }

    private createNewDashboard(): void {
        const saveDashboardFormDialogRef = this.modalService.open<void, SaveDashboardFormComponent>(
            SaveDashboardFormComponent,
            { windowClass: 'save-dialog' });

        saveDashboardFormDialogRef.componentInstance.clientCode = this.clientCode;
        saveDashboardFormDialogRef.componentInstance.selectedOption = 'new';
        saveDashboardFormDialogRef.componentInstance.isReadOnly = false;
        saveDashboardFormDialogRef.componentInstance.isInternal = this.isInternal;
        saveDashboardFormDialogRef.componentInstance.entitlements = this.entitlements;
        saveDashboardFormDialogRef.componentInstance.closeForm
            .subscribe({
                next: () => saveDashboardFormDialogRef.close(),
            });
        saveDashboardFormDialogRef.componentInstance.formSubmitted
            .subscribe({
                next: (form: { details: DashboardDetails, sharing: Sharing }) => {
                    this.invokeCreateDashboard(
                        form.details,
                        form.sharing,
                        saveDashboardFormDialogRef);
                },
            });

        saveDashboardFormDialogRef.result.then(() => {}, () => {});
    }

    private initTransitionEvents(): void {
        if (!this.leftNav) {
            return;
        }
        const leftNav = this.leftNav.nativeElement;
        const transitionEvent = this.getTransitionEvent(leftNav);
        if (transitionEvent) {
            leftNav.addEventListener(transitionEvent, () => {
                if (this.manager.getWorkspace()) {
                    const layoutHandler = this.manager.getWorkspaceLayoutHandler();
                    layoutHandler?.onWindowResize(undefined, true);
                }
            });
        }
    }

    private initializeViewTiles(params: ParamMap, viewTiles: ViewTile[]): void {
        this.tiles = viewTiles;

        if (this.currentView) {
            if (!viewTiles.length) {
                this.manager.removeWorkspace();
                ignorePromise(this.navigateToBoardAndMode(undefined, 'view'));
            } else if (!viewTiles.some((t) => t.links.some((l) => l.id === this.currentView?.id))) {
                ignorePromise(this.navigateToBoardAndMode(this.tiles[0].selectedLink?.id, this.inPresentationMode ? 'view' : 'edit'));
            }
        }

        const viewId = Number(params.get('dashboardId'));
        this.currentView = undefined;
        for (const tile of this.tiles) {
            if (tile.links.map((l) => l.id).includes(viewId)) {
                tile.selectedLink = tile.links.find((l) => l.id === viewId);
                this.currentView = tile.selectedLink;
                break;
            }
        }

        this.inPresentationMode = params.get('mode') === 'view';
        this.bringDashboardIntoView();

        this.isGlobalEditPartial = !!this.currentView?.isGlobal && !!this.entitlements?.hasGlobalEditPartial();
    }

    private getTransitionEvent(leftNavElement: HTMLElement): string {
        const transitions: Record<string, string> = {
            transition: 'transitionend',
            MozTransition: 'transitionend',
            WebkitTransition: 'webkitTransitionEnd',
        };

        for (const t in transitions) {
            if (leftNavElement.style[t as keyof CSSStyleDeclaration] !== undefined) {
                return transitions[t];
            }
        }

        return '';
    }

    private launchCopyDashboardModal(): void {
        const saveDashboardFormDialogRef = this.modalService.open<void, SaveDashboardFormComponent>(
            SaveDashboardFormComponent,
            { windowClass: 'save-dialog' });

        saveDashboardFormDialogRef.componentInstance.clientCode = this.clientCode;
        saveDashboardFormDialogRef.componentInstance.selectedOption = 'edit';
        saveDashboardFormDialogRef.componentInstance.isReadOnly = false;
        saveDashboardFormDialogRef.componentInstance.isDashboardCopy = true;
        saveDashboardFormDialogRef.componentInstance.isGlobalEditPartial = this.isGlobalEditPartial;
        saveDashboardFormDialogRef.componentInstance.isInternal = this.isInternal;
        saveDashboardFormDialogRef.componentInstance.entitlements = this.entitlements;
        const dashboardToCopy = this.dashboardService.getCurrentDashboard();
        if (dashboardToCopy) {
            saveDashboardFormDialogRef.componentInstance.dashboard = new DashboardModel(dashboardToCopy).cloneForCopy();
        }

        saveDashboardFormDialogRef.componentInstance.closeForm
            .subscribe({
                next: () => saveDashboardFormDialogRef.close(),
            });

        saveDashboardFormDialogRef.componentInstance.formSubmitted.subscribe({
            next: (form: { details: DashboardDetails, sharing: Sharing }) => {
                const payload = buildUpdateDashboardInfoRequest(form.details);
                this.dashboardService.copyDashboard(this.clientCode ?? '', form.details.id ?? 0, payload)
                    // This is a bit of a workaround, but it is complicated to handle this case in a generalized manner,
                    // when the result still can come from the MW as well
                    .pipe(
                        switchMap((dashboardId) => {
                            return this.dashboardNavService.fetchOpenViewTiles(this.clientCode ?? '')
                                .pipe(map(() => dashboardId));
                        }),
                    )
                    .subscribe({
                        next: (dashboardId) => {
                            saveDashboardFormDialogRef.close();
                            ignorePromise(this.navigateToBoardAndMode(dashboardId, 'edit'));
                        },
                    });
            },
        });

        saveDashboardFormDialogRef.result.then(() => {}, () => {});
    }

    private invokeCreateDashboard(
        details: DashboardDetails,
        sharing: Sharing,
        dialogRef: ModalDialogRef<void, SaveDashboardFormComponent>,
    ): void {
        dialogRef.componentInstance.isSaveDashboardInvoked = true;

        // When we are done with the migration we can create the dashboard details much earlier
        // in convertFormDataToDashboardDetails method in the component
        const payload = buildCreateDashboardRequest(details);
        this.dashboardService.createDashboard(this.clientCode ?? '', payload)
            // This is a bit of a workaround, but it is complicated to handle this case in a generalized manner,
            // when the result still can come from the MW as well
            .pipe(
                switchMap((dashboard) => {
                    return this.dashboardNavService.fetchOpenViewTiles(this.clientCode ?? '')
                        .pipe(map(() => dashboard));
                }),
            )
            .pipe(
                switchMap((updatedDashboard: DashboardDetails) => {
                    if (!this.clientCode || !updatedDashboard.id) {
                        console.warn('Either the client code is not set or createDashboard did not return an id, which should not happen');
                    }
                    return this.sharingService.updateSharing(this.clientCode ?? '', updatedDashboard.id ?? 0, sharing, this.clientCode ?? '')
                        .pipe(map(() => updatedDashboard));
                }),
                switchMap((updatedDashboard: DashboardDetails) =>
                    this.dashboardService.openDashboard(this.clientCode ?? '', updatedDashboard.id ?? 0)
                        .pipe(map(() => updatedDashboard)),
                ),
            )
            .subscribe({
                next: (updatedDashboard: DashboardDetails) => {
                    this.manager.updateWidgetsVersion(
                        updatedDashboard.widgets as { id: number, version: number }[],
                    );

                    dialogRef.close();
                    this.dirtyFlagService.exitDirtyState();
                    if (!this.currentView || updatedDashboard.id !== this.currentView.id) {
                        ignorePromise(this.navigateToBoardAndMode(updatedDashboard.id, 'edit'));
                    }
                },
                error: (error) => this.onDashboardSaveError(error, dialogRef),
            });
    }

    private onDashboardSaveError(e: unknown, form?: ModalDialogRef<void, SaveDashboardFormComponent>): void {
        if (!Object.hasOwnProperty.call(e, '_body')) {
            return;
        }

        const error = e as { _body?: string | { message: string } };
        if (!error._body) {
            return;
        }

        const errorDetails = typeof error._body === 'string' ? JSON.parse(error._body) as { message: string } : error._body;
        if (errorDetails.message === 'Dashboard name is already in use') {
            return form?.componentInstance.nameUnavailable();
        }

        if (errorDetails.message === 'Dashboard version is stale') {
            if (form) {
                form.close();
            }

            this.handleStaleDashboardVersion(errorDetails.message);
        }
    }

    private async navigateToBoardAndMode(dashboardId: string | number | undefined, mode: string): Promise<boolean> {
        return this.router.navigate([`../../${mode}/${dashboardId ?? 'none'}`], { relativeTo: this.route });
    }

    private addWidgetToDashboard(widgetOnBoard: WidgetOnBoard): void {
        if (!widgetOnBoard.widget) {
            return;
        }

        const widget = WidgetConfigurationManager.getWidgetConfigForWorkspace(
            widgetOnBoard.widget,
            this.layoutService.getWidgetDefaultPosition(widgetOnBoard.widget.widgetPosition));

        widget.styleClasses = widgetOnBoard.widget.enableWidgetHeader ? '' : 'hide-header';
        this.manager.createWidget(widget);
        this.manager.getWorkspace()?.extraParameters?.widgets.push(widgetOnBoard.widget);

        const dashboardDetails = this.dashboardService.getCurrentDashboard();
        if (dashboardDetails?.widgetsOnBoard) {
            dashboardDetails.widgetsOnBoard.push(widgetOnBoard);
        }

        widgetOnBoard.widget.widgetPosition = this.layoutService.getWidgetPositionById(widget.id);
        this.manager.setWidgetExtraPreferences(widget.id, widgetOnBoard.widget);
        this.widgetOnBoardService.notifyForWidgetAddedOnDashboard();
    }

    private bringDashboardIntoView(): void {
        if (!this.currentView) {
            return;
        }

        this.changeDetectorRef.detectChanges();
        const dashboardListElement = this.dashboardList?.nativeElement as HTMLElement;
        const dashboardElement: HTMLElement | null = dashboardListElement.querySelector(`#db_${this.currentView.id}`);
        if (!dashboardElement) {
            return;
        }

        const dashboardElementParent = dashboardElement.parentElement as HTMLElement;
        const { offsetTop, clientHeight } = dashboardElement;
        const { clientHeight: parentClientHeight, scrollTop: parentScrollTop } = dashboardElementParent;
        if (offsetTop + clientHeight > parentClientHeight + parentScrollTop) {
            dashboardElement.scrollIntoView(false);
        } else if (this.checkElementOutOfBound(dashboardElement, dashboardElementParent)) {
            dashboardElement.scrollIntoView(true);
        }
    }

    private checkElementOutOfBound(dashboardElement: HTMLElement, dashboardElementParent: HTMLElement): boolean {
        return dashboardElement.offsetTop < dashboardElementParent.scrollTop ||
            (dashboardElementParent.scrollTop < (dashboardElement.clientHeight / 2) &&
            dashboardElement.offsetTop < dashboardElement.clientHeight);
    }

    private discardChangesCallback(): void {
        this.manager.selectWidget(false, undefined);
        this.dirtyFlagService.exitDirtyState();
        this.revertChanges.emit();
        this.createNewDashboard();
    }

    private handleStaleDashboardVersion(message: string): void {
        this.confirmationPopupService.showConfirmationPopup({ message }, true).subscribe({
            next: (action) => {
                if (action === 'confirm') {
                    this.revert();
                }
            },
        });
    }

    private revert(): void {
        const dashboardId = this.manager.getCurrentDashboardId() ?? '';
        this.manager.removeAllWidgets();
        this.revertDashboardService.revert(dashboardId, this.layoutService.getWorkspaceMode(), this.clientCode ?? '');
    }
}

function ignorePromise(promise: Promise<unknown>): void {
    promise.then(() => {}, () => {});
}
